import { useEffect, useRef, useState } from 'react';
import { Select } from 'antd';
import { Icon } from 'Components';
import Alert from '../components/Alert';
import StepFooter from './StepFooter';

type TestConfigurationProps = {
  onBack: () => void;
  onNext: (emails: string[]) => void;
  stepErrorMessage?: string | null;
  setStepErrorMessage: (message: | null) => void
};

const CloseIcon = () => (
  <Icon type="campaignStepClose" />
);

const tagRender = (props: any) => {
  const { label, closable, onClose } = props;

  return (
    <span className="ml-chip">
      <span className="ml-chip-text">{label}</span>

      {closable && (
        <span
          className="ml-chip-close"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={onClose}
        >
          <CloseIcon />
        </span>
      )}
    </span>
  );
};

const TestConfiguration = ({ onBack, onNext, stepErrorMessage, setStepErrorMessage }: TestConfigurationProps) => {
  const [recipients, setRecipients] = useState<string[]>([]);
  const [emailError, setEmailError] = useState<string | null>(null);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const [searchValue, setSearchValue] = useState<string>("");

  const hasInvalidEmail = (emails: string[]) =>
    emails.some(email => !emailRegex.test(email));

  const handleSearch = (value: string) => {
    setSearchValue(value);
    resetErrorMessage();
  }

  const resetErrorMessage = () => {
    if (!!stepErrorMessage) {
      setStepErrorMessage(null);
    }
    if (!!emailError) {
      setEmailError(null);
    }
  }

  const handleChange = (values: string[]) => {
    setRecipients(values);
    if (hasInvalidEmail(values)) {
      setEmailError("Enter a valid email address");
    } else {
      setEmailError(null);
    }
  };

  useEffect(() => {
    setEmailError(stepErrorMessage || null);
  }, [stepErrorMessage]);

  return (
    <>
      <div className="test-configuration-container step-card">
        <div className="card-header">
          <div className="card-title">Test Configuration</div>
          <div className="card-subtitle">
            Select users to receive a test phishing email to verify setup before the full campaign
          </div>
        </div>

        <div className="test-config-form">
          <label className="form-label">
            Test recipients <span className="required-asterisk">*</span>
          </label>

          <div className="email-input-wrapper">
            <Select
              mode="tags"
              size="large"
              value={recipients}
              onSelect={(value) => handleChange([...recipients, value])}
              onSearch={handleSearch}
              onChange={(value) => {
                setRecipients(value);
                resetErrorMessage();
              }}
              placeholder="Enter one or more email addresses"
              tokenSeparators={[',', ' ']}
              onDeselect={(value) => {
                const trimmed = searchValue.trim().toLowerCase();
                if (trimmed === value.trim().toLowerCase() && recipients.includes(value)) {
                  setRecipients([...recipients.filter((v) => v !== value), value]);
                  setSearchValue("");
                } else {
                  setRecipients(recipients.filter((v) => v !== value));
                }
              }}
              className="ml-input-chip"
              dropdownClassName='campaign-setup-filter-option-select'
              style={{ width: '100%' }}
              notFoundContent={null}
              tagRender={tagRender}></Select>{emailError && (
                <div className="ml-input-help-text">
                  {emailError}
                </div>
              )}
          </div>
        </div>

        <div className='alert-section'>
          <Alert subtitle='Test emails are sent within a few minutes after the test campaign is started.' />
        </div>
      </div>
      <StepFooter
        onBack={onBack}
        onNext={() => onNext(recipients)}
        nextBtnDisabled={(recipients.length === 0 || !!emailError)}
      />
    </>
  );
};

export default TestConfiguration;